import { EventSourcePolyfill } from "../../utils/eventsource";
import { apiController } from "../controllers/api-controller";
import { clientController } from "../controllers/client-controller";
import { autorun, computed, observable } from "mobx";
import { capitalize, isEmpty, safeParseJSON } from "../../utils/helpers";
import { endpointConfig } from "../../config/api-config";
import { apiService } from "./api-service";
import { msgService } from "./messaging-service";
import { txConfig } from "../../config/tx-config";
import { env } from "../../config/variable-config";

class TransactionService {
  @observable hasActivity = false;
  sseEventListeners = [];
  logEventListeners = [];

  syncTxLogsDebounce;

  @computed get OAuth2Data() {
    return (
      clientController.client.oauth &&
      `${capitalize(clientController.client.oauth["token_type"])} ${
        clientController.client.oauth["access_token"]
      }`
    );
  }
  @computed get options() {
    return { headers: { Authorization: this.OAuth2Data } };
  }
  @computed get endpoint() {
    return (
      txConfig.baseUrl +
      endpointConfig.transaction_sse(clientController.clientId)
    );
  }
  @computed get lastTxId() {
    return (
      (clientController.client.user && clientController.client.user.txId) || 0
    );
  }

  get connected() {
    return (
      !!this.eventSource &&
      this.eventSource.readyState >= 1 &&
      this.eventSource.readyState <= 3
    );
  }

  constructor() {
    this._initializeTx();
  }

  _initializeTx = () => {
    this.disposer = autorun(() => {
      if (
        !!clientController &&
        clientController.initialized &&
        clientController.loginState &&
        !clientController.isSyncMode
      ) {
        !this.connected && this.connectEventSource();
      } else {
        this.disconnectEventSource();
      }
    });
  };

  _registerEvents = () => {
    if (!this.eventSource) return;
    this.eventSource.addEventListener("open", this.onOpen);
    this.eventSource.addEventListener("message", this.onMessage);
    this.eventSource.addEventListener("error", this.onError);
  };

  connectEventSource = () => {
    // console.log("connect");
    // if (clientController.baseSwitched) return;
    if (this.eventSource) this.disconnectEventSource();
    this.eventSource = new EventSourcePolyfill(this.endpoint, this.options);
    if (window && env !== "prod") window.eventSource = this.eventSource;
    this._registerEvents();
    return this.syncTxLogs();
  };

  disconnectEventSource = () => {
    // console.log("disconnect");
    if (!this.eventSource) return;
    this.eventSource.close();
    this.eventSource = undefined;
  };

  onOpen = message => {
    console.log("eventSource", message.type, message.data);
  };

  onMessage = message => {
    const data = safeParseJSON(message.data) || message.data;
    console.log("eventSource", message.type, data);

    this.addActivity();

    for (let eventListener of this.sseEventListeners) {
      const { listener } = eventListener;
      typeof listener === "function" && listener(data);
    }
    // Tx log update SSE
    if (data.type === "tx") {
      return this.handlers[data.message]().finally(this.removeActivity);
    }
    // Messaging service SSE
    if (data.type === "msg") {
      return msgService.onMessage(data).finally(this.removeActivity);
    }
    // Proactive chat to wrapper SSE
    if (data.type === "pc") {
      return msgService
        .onProactiveChatSse(message)
        .finally(this.removeActivity);
    }

    return this.removeActivity();
  };

  onError = error => {
    console.warn("txService error", error);
  };

  addActivity = () => (this.hasActivity = true);

  removeActivity = () => (this.hasActivity = false);

  get handlers() {
    return {
      new: this.syncTxLogs,
      logout: () => {
        clientController.clearStorage();
        return clientController.execLogout(null, true);
      },
      refresh: () => {
        return clientController.fullClientReset();
      }
    };
  }

  syncTxLogs = async () => {
    clearTimeout(this.syncTxLogsDebounce);
    this.syncTxLogsDebounce = setTimeout(
      () =>
        apiService
          .async("GET", {
            endpoint: endpointConfig.logs_after_tx_id(this.lastTxId)
          })
          .then(response => {
            const logs = response.data;
            if (!logs) return;

            return this.processTxLogs(logs)
              .then(this.processUpdates)
              .then(this.updateLastTxId)
              .catch(console.warn);
          }),
      2000
    );
  };

  processTxLogs = async logs => {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    logs = logs.sort(this.sortTxId);

    const updates = {};
    const final = [];

    for (let log of logs) {
      const data = log.data && safeParseJSON(log.data);
      !Array.isArray(updates[data.entity]) && (updates[data.entity] = []);
      if (!updates[data.entity].includes(data.id)) {
        updates[data.entity].push(data.id);
        final.push(data);
      }
    }

    const lastTxId = logs[logs.length - 1].txId;
    return [final, lastTxId];
  };

  processUpdates = async ([updates, lastTxId]) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    const process = update => {
      if (!update.entity || !update.id) return;
      for (let eventListener of this.logEventListeners) {
        const { listener } = eventListener;
        typeof listener === "function" && listener(update);
      }
      return this.updateHandlers[update.entity.toLowerCase()](update.id);
    };
    await Promise.all(updates.map(process)).catch(console.warn);
    return lastTxId;
  };

  updateHandlers = {
    group: async groupId => {
      const member = clientController.findMembers(
        m => m.groupId === groupId
      )[0];
      if (isEmpty(member)) return;

      return apiController.getGroupById(groupId, member.id).then(group => {
        if (group.id) {
          return clientController.updateGroup(group, groupId);
        }
      });
    },
    member: async memberId => {
      return apiController.getMemberById(memberId).then(member => {
        if (member.id) {
          return clientController.updateMember(member, memberId);
        } else {
          return clientController.removeMember(memberId);
        }
      });
    },
    topic: async topicId => {
      return apiController.getTopicById(topicId).then(topic => {
        if (topic && topic.id) {
          return clientController.updateTopic(topic, topicId);
        } else {
          return clientController.removeTopic(topicId);
        }
      });
    },
    profile: async profileId => {
      return apiController
        .getProfileById(profileId)
        .then(profile => clientController.updateProfile(profile, profileId));
    },
    user: async userId => {
      // return apiController.postLogin().then(clientController.updateUser);
    }
  };

  updateLastTxId = txId => {
    console.log("set lastTxId:", txId);
    // UNSAFE!!
    clientController.setLastTxId(txId);
  };

  sortTxId = (a, b) => {
    if (!a.txId || !b.txId) return 0;
    return a.txId - b.txId;
  };

  addSSEEventListener = (name, listener) => {
    if (!name || typeof listener !== "function") return;
    if (this.sseEventListeners.some(l => l.name === name)) return;

    return this.sseEventListeners.push({
      name,
      listener
    });
  };

  addLogEventListener = (name, listener) => {
    if (!name || typeof listener !== "function") return;
    if (this.logEventListeners.some(l => l.name === name)) return;

    return this.logEventListeners.push({
      name,
      listener
    });
  };

  removeSSEEventListener = name => {
    if (!name) return;
    const index = this.sseEventListeners.findIndex(l => l.name === name);
    if (index < 0) return;
    return this.sseEventListeners.splice(index, 1);
  };

  removeLogEventListener = name => {
    if (!name) return;
    const index = this.logEventListeners.findIndex(l => l.name === name);
    if (index < 0) return;
    return this.logEventListeners.splice(index, 1);
  };
}

const txService = new TransactionService();

// For development;
if (window && env !== "prod") window.txService = txService;

export { txService };
